import React, { useState, useEffect } from 'react';
import {
  Card, Typography, Tag, Descriptions, Button, Spin, Empty,
  Divider, Timeline, Input, Space, Alert, Modal, Row, Col, Tooltip,
  Upload, Steps, message, Drawer,
} from 'antd';
import {
  ArrowLeftOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ClockCircleOutlined, SyncOutlined, FileTextOutlined,
  FilePdfOutlined, FileImageOutlined, PaperClipOutlined,
  EyeOutlined, DeleteOutlined, AuditOutlined,
  ExclamationCircleOutlined, BankOutlined, LeftOutlined,
  RightOutlined, DownloadOutlined, InboxOutlined,
  RedoOutlined, LoadingOutlined, HistoryOutlined,
  UploadOutlined, FileAddOutlined, EditOutlined,
  CheckOutlined, CloseOutlined, SaveOutlined,
  ScheduleOutlined, ToolOutlined, CarOutlined, TeamOutlined,
  PauseCircleOutlined, PlayCircleOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/* ON HOLD accent */
const HOLD = '#d46b08';
const HOLD_SOFT = '#fff7e6';
const HOLD_LINE = '#ffd591';

const formatAmount = (val) => {
  if (!val && val !== 0) return '-';
  return `AED ${Number(val).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDateTime = (val) => {
  if (!val) return '-';
  return new Date(val).toLocaleString('en-GB', {
    timeZone: 'Asia/Dubai', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
};

const statusConfig = {
  pending_review: { color: 'gold', icon: <ClockCircleOutlined />, label: 'Pending Review' },
  under_review: { color: 'processing', icon: <SyncOutlined />, label: 'Under Review' },
  on_hold: { color: 'orange', icon: <PauseCircleOutlined />, label: 'On Hold' },
  approved: { color: 'success', icon: <CheckCircleOutlined />, label: 'Approved' },
  rejected: { color: 'error', icon: <CloseCircleOutlined />, label: 'Rejected' },
  deleted: { color: 'default', icon: <DeleteOutlined />, label: 'Deleted' },
};

const docTypeConfig = {
  invoice: { label: 'Invoice', color: '#fff1f0', border: '#ffa39e' },
  delivery_note: { label: 'Delivery Note', color: '#f0f7ff', border: '#d6e4f0' },
  timesheet: { label: 'Timesheet', color: '#fffbe6', border: '#ffe58f' },
  attachment: { label: 'Attachment', color: '#fafbfd', border: '#e8ecf1' },
  revised_invoice: { label: 'Revised Invoice', color: '#f9f0ff', border: '#d3adf7' },
  revised_delivery_note: { label: 'Revised DN', color: '#f0f7ff', border: '#91caff' },
  revised_timesheet: { label: 'Revised Timesheet', color: '#fffbe6', border: '#ffd666' },
};

const categoryConfigMap = {
  material: { color: '#1a6ed8', label: 'Material', icon: <ToolOutlined /> },
  transportation: { color: '#d48806', label: 'Transportation', icon: <CarOutlined /> },
  labour_supply: { color: '#531dab', label: 'Labour Supply / Subcontractor', icon: <TeamOutlined /> },
};

const auditActionLabels = {
  submitted: { color: '#1a6ed8', label: 'Submitted' },
  approved: { color: '#52c41a', label: 'Approved' },
  rejected: { color: '#ff4d4f', label: 'Rejected' },
  edited: { color: '#faad14', label: 'Edited' },
  deleted: { color: '#8c8c8c', label: 'Deleted' },
  revision_submitted: { color: '#722ed1', label: 'Revision Submitted' },
  status_changed: { color: '#1a6ed8', label: 'Status Changed' },
  held: { color: HOLD, label: 'Put On Hold' },
  hold_released: { color: '#52c41a', label: 'Hold Released' },
};

const SubmissionDetail = ({ submissionId, isAccounts, username, onBack, onReviewComplete }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewRemarks, setReviewRemarks] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  // Hold (accounts only)
  const [holdRemarks, setHoldRemarks] = useState('');
  const [holdLoading, setHoldLoading] = useState(false);

  // NEW: Drawer-based preview instead of landscape modal
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewDocs, setPreviewDocs] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [activeDocIndex, setActiveDocIndex] = useState(0);

  // Revision state
  const [revisionFile, setRevisionFile] = useState(null);
  const [revisionDnFile, setRevisionDnFile] = useState(null);
  const [revisionTsFile, setRevisionTsFile] = useState(null);
  const [revisionSubmitting, setRevisionSubmitting] = useState(false);
  const [revisionStep, setRevisionStep] = useState(0);

  // LPO Edit state
  const [editingLpo, setEditingLpo] = useState(false);
  const [lpoValue, setLpoValue] = useState('');
  const [lpoSaving, setLpoSaving] = useState(false);

  // Inline field edit state (for editable fields)
  const [editingField, setEditingField] = useState(null);
  const [editFieldValue, setEditFieldValue] = useState('');
  const [editFieldSaving, setEditFieldSaving] = useState(false);

  useEffect(() => { fetchDetail(); }, [submissionId]);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/procurement/submissions/${submissionId}`);
      setData(res.data.submission);
    } catch (err) {
      console.error('Failed to fetch submission:', err);
    } finally {
      setLoading(false);
    }
  };

  /* Mirrors build_hold_message() in procurement_db.py — used for the preview */
  const buildHoldMessage = (note) => {
    const vendor = (data?.vendor_name || '').trim() || 'Unknown supplier';
    const inv = (data?.invoice_number || '').trim() || data?.submission_ref || '—';
    const base = `Invoice ${inv} from ${vendor} is currently ON HOLD with Accounts and is under review.`;
    const n = (note || '').trim();
    return n ? `${base} Note: ${n}` : base;
  };

  // ── INLINE FIELD EDIT ──
  const startEditField = (fieldName, currentValue) => {
    setEditingField(fieldName);
    setEditFieldValue(currentValue || '');
  };

  const cancelEditField = () => {
    setEditingField(null);
    setEditFieldValue('');
  };

  const saveEditField = async (fieldName) => {
    setEditFieldSaving(true);
    try {
      const res = await axios.patch(`${API_BASE_URL}/api/procurement/submissions/${submissionId}`, {
        [fieldName]: editFieldValue.trim(),
        edited_by: username,
      });
      if (res.data.success) {
        message.success(`${fieldName.replace(/_/g, ' ')} updated`);
        setEditingField(null);
        setEditFieldValue('');
        await fetchDetail();
      } else {
        message.error(res.data.message || 'Update failed');
      }
    } catch (err) {
      message.error('Failed to update');
    } finally {
      setEditFieldSaving(false);
    }
  };

  // ── LPO EDIT ──
  const startEditLpo = () => {
    setLpoValue(data?.lpo_number || '');
    setEditingLpo(true);
  };

  const cancelEditLpo = () => {
    setEditingLpo(false);
    setLpoValue('');
  };

  const saveLpo = async () => {
    if (!lpoValue.trim()) {
      message.warning('LPO number cannot be empty');
      return;
    }
    setLpoSaving(true);
    try {
      const res = await axios.patch(`${API_BASE_URL}/api/procurement/submissions/${submissionId}`, {
        lpo_number: lpoValue.trim(),
        edited_by: username,
      });
      if (res.data.success) {
        message.success('LPO number updated successfully');
        setEditingLpo(false);
        await fetchDetail();
      } else {
        message.error(res.data.message || 'Failed to update LPO');
      }
    } catch (err) {
      message.error('Failed to update LPO number');
    } finally {
      setLpoSaving(false);
    }
  };

  // ── Editable field renderer ──
  const renderEditableField = (fieldName, displayValue, options = {}) => {
    const { isTag, tagColor, isBold, isAmount } = options;
    const canEdit = data?.status !== 'approved' && data?.status !== 'deleted';
    const isEditing = editingField === fieldName;

    if (isEditing) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Input
            size="small"
            value={editFieldValue}
            onChange={(e) => setEditFieldValue(e.target.value)}
            onPressEnter={() => saveEditField(fieldName)}
            style={{ borderRadius: 6, flex: 1, maxWidth: 220 }}
            autoFocus
          />
          <Button
            type="text" size="small" icon={<CheckOutlined />}
            loading={editFieldSaving}
            onClick={() => saveEditField(fieldName)}
            style={{ color: '#52c41a', padding: '0 4px' }}
          />
          <Button
            type="text" size="small" icon={<CloseOutlined />}
            onClick={cancelEditField}
            style={{ color: '#ff4d4f', padding: '0 4px' }}
          />
        </div>
      );
    }

    const content = isTag && displayValue
      ? <Tag color={tagColor || 'blue'} style={{ borderRadius: 4 }}>{displayValue}</Tag>
      : isBold
        ? <Text strong style={{ color: '#1a3353' }}>{displayValue || '-'}</Text>
        : <span>{displayValue || '-'}</span>;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {content}
        {canEdit && (
          <Tooltip title={`Edit ${fieldName.replace(/_/g, ' ')}`}>
            <Button
              type="text" size="small" icon={<EditOutlined />}
              onClick={() => startEditField(fieldName, displayValue)}
              style={{ color: '#8dabc4', padding: '0 4px', fontSize: 12, opacity: 0.7 }}
              className="edit-field-btn"
            />
          </Tooltip>
        )}
      </div>
    );
  };

  // ── REVIEW ──
  const handleReview = async (decision) => {
    if (decision === 'rejected' && !reviewRemarks.trim()) {
      Modal.warning({ title: 'Remarks Required', content: 'Please provide remarks when rejecting a submission.' });
      return;
    }
    Modal.confirm({
      title: `${decision === 'approved' ? 'Approve' : 'Reject'} this submission?`,
      icon: decision === 'approved' ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div>
          <Text>Submission: <Text strong>{data?.submission_ref}</Text></Text><br />
          <Text>Vendor: <Text strong>{data?.vendor_name}</Text></Text><br />
          <Text>Amount: <Text strong>{formatAmount(data?.total_amount)}</Text></Text>
          {data?.status === 'on_hold' && <div style={{ marginTop: 8, color: HOLD }}>This also releases the current hold.</div>}
          {reviewRemarks && <div style={{ marginTop: 8 }}>Remarks: <Text italic>{reviewRemarks}</Text></div>}
        </div>
      ),
      okText: decision === 'approved' ? 'Yes, Approve' : 'Yes, Reject',
      okButtonProps: { danger: decision === 'rejected' },
      onOk: async () => {
        setReviewLoading(true);
        try {
          await axios.post(`${API_BASE_URL}/api/procurement/submissions/${submissionId}/review`, { decision, remarks: reviewRemarks.trim(), reviewed_by: username });
          message.success(`Submission ${decision} successfully`);
          setReviewRemarks('');
          await fetchDetail();
          if (onReviewComplete) onReviewComplete();
        } catch { message.error('Review failed'); }
        finally { setReviewLoading(false); }
      },
    });
  };

  // ── HOLD / RELEASE (accounts) ──
  const handleHold = () => {
    const note = holdRemarks.trim();
    Modal.confirm({
      title: 'Put this invoice on hold?',
      icon: <PauseCircleOutlined style={{ color: HOLD }} />,
      width: 520,
      content: (
        <div>
          <Text>Submission: <Text strong>{data?.submission_ref}</Text></Text>
          <div style={{ marginTop: 10, fontSize: 11, color: '#8dabc4', textTransform: 'uppercase', letterSpacing: 0.5 }}>The uploader will see</div>
          <div style={{ marginTop: 4, padding: '8px 12px', background: HOLD_SOFT, border: `1px solid ${HOLD_LINE}`, borderRadius: 6, fontSize: 13, lineHeight: 1.5 }}>
            {buildHoldMessage(note)}
          </div>
        </div>
      ),
      okText: 'Put On Hold',
      okButtonProps: { style: { background: HOLD, borderColor: HOLD, color: '#fff' } },
      onOk: async () => {
        setHoldLoading(true);
        try {
          const res = await axios.post(`${API_BASE_URL}/api/procurement/submissions/${submissionId}/hold`, {
            remarks: note, held_by: username,
          });
          if (res.data.success) {
            message.success('Invoice put on hold');
            setHoldRemarks('');
            await fetchDetail();
            if (onReviewComplete) onReviewComplete();
          } else message.error(res.data.message || 'Failed to put on hold');
        } catch (err) { message.error(err.response?.data?.message || 'Failed to put on hold'); }
        finally { setHoldLoading(false); }
      },
    });
  };

  const handleReleaseHold = () => {
    Modal.confirm({
      title: 'Release this hold?',
      icon: <PlayCircleOutlined style={{ color: '#52c41a' }} />,
      content: (
        <div>
          <Text>Submission: <Text strong>{data?.submission_ref}</Text></Text>
          <div style={{ marginTop: 8 }}>It goes back to <Text strong>Pending Review</Text> and the hold note is cleared for the uploader.</div>
        </div>
      ),
      okText: 'Release Hold',
      onOk: async () => {
        setHoldLoading(true);
        try {
          const res = await axios.post(`${API_BASE_URL}/api/procurement/submissions/${submissionId}/release-hold`, {
            remarks: holdRemarks.trim(), released_by: username,
          });
          if (res.data.success) {
            message.success('Hold released — back in the review queue');
            setHoldRemarks('');
            await fetchDetail();
            if (onReviewComplete) onReviewComplete();
          } else message.error(res.data.message || 'Failed to release hold');
        } catch (err) { message.error(err.response?.data?.message || 'Failed to release hold'); }
        finally { setHoldLoading(false); }
      },
    });
  };

  // ── SUBMIT REVISION ──
  const handleSubmitRevision = async () => {
    if (!revisionFile) { message.warning('Please select a revised invoice file'); return; }

    setRevisionSubmitting(true);
    setRevisionStep(0);

    const formData = new FormData();
    formData.append('invoice_file', revisionFile);
    formData.append('submitted_by', username);

    // Category-specific files
    if (data.category === 'material' && revisionDnFile) {
      formData.append('delivery_note_file', revisionDnFile);
    }
    if (data.category === 'transportation' && revisionTsFile) {
      formData.append('timesheet_file', revisionTsFile);
    }

    const t1 = setTimeout(() => setRevisionStep(1), 2000);
    const t2 = setTimeout(() => setRevisionStep(2), 5000);

    try {
      const res = await axios.post(`${API_BASE_URL}/api/procurement/submissions/${submissionId}/revise`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000,
      });
      clearTimeout(t1); clearTimeout(t2);
      setRevisionStep(3);

      if (res.data.success) {
        message.success(`Revision #${res.data.revision_number} submitted — ${res.data.submission_ref}`);
        setRevisionFile(null);
        setRevisionDnFile(null);
        setRevisionTsFile(null);
        setRevisionStep(0);
        await fetchDetail();
        if (onReviewComplete) onReviewComplete();
      } else {
        message.error(res.data.message || 'Revision failed');
      }
    } catch (err) {
      clearTimeout(t1); clearTimeout(t2);
      message.error(err.response?.data?.message || 'Revision failed');
    } finally {
      setRevisionSubmitting(false);
    }
  };

  // ── PREVIEW ALL (now opens Drawer) ──
  const handlePreviewAll = async (startIndex) => {
    setPreviewLoading(true);
    setPreviewVisible(true);
    setActiveDocIndex(startIndex || 0);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/procurement/submissions/${submissionId}/preview-all`);
      if (res.data.success) setPreviewDocs(res.data.documents || []);
      else message.error('Failed to load previews');
    } catch { message.error('Failed to load previews'); }
    finally { setPreviewLoading(false); }
  };

  const handleDownload = (doc) => {
    const url = doc?.download_url || doc?.url;
    if (url) { const a = document.createElement('a'); a.href = url; a.download = doc.filename || 'document'; a.target = '_blank'; document.body.appendChild(a); a.click(); document.body.removeChild(a); }
  };

  // ═══════════════════════════════════════════
  //  DRAWER PREVIEW CONTENT (Vertical Layout)
  // ═══════════════════════════════════════════

  const renderDrawerPreview = () => {
    if (previewLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Spin size="large" />
        </div>
      );
    }
    if (!previewDocs.length) return <Empty description="No documents" />;

    const doc = previewDocs[activeDocIndex];
    if (!doc?.url) return <Empty description="URL not available" />;

    const ft = (doc.file_type || '').toLowerCase();
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'tiff', 'tif'].includes(ft);
    const isPdf = ft === 'pdf';

    return (
      <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

        {/* ── LEFT: Document List Sidebar ── */}
        <div style={{
          width: 220, minWidth: 220, borderRight: '1px solid #e8ecf1',
          background: '#f8fafc', display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '16px 14px 12px', borderBottom: '1px solid #e8ecf1',
            background: '#fff',
          }}>
            <Text strong style={{ fontSize: 13, color: '#1a3353', display: 'block' }}>
              Documents
            </Text>
            <Text style={{ fontSize: 11, color: '#8dabc4' }}>
              {previewDocs.length} file{previewDocs.length !== 1 ? 's' : ''}
            </Text>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 8px' }}>
            {previewDocs.map((d, idx) => {
              const dc = docTypeConfig[d.doc_type] || docTypeConfig.attachment;
              const active = idx === activeDocIndex;
              return (
                <div
                  key={d.id}
                  onClick={() => setActiveDocIndex(idx)}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                    marginBottom: 4, transition: 'all 0.15s',
                    background: active ? '#1a6ed8' : 'transparent',
                    border: active ? '1px solid #1a6ed8' : '1px solid transparent',
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = '#edf2f7'; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: 7, flexShrink: 0,
                    background: active ? 'rgba(255,255,255,0.2)' : dc.color,
                    border: `1px solid ${active ? 'rgba(255,255,255,0.3)' : dc.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14,
                  }}>
                    {active
                      ? <FilePdfOutlined style={{ color: '#fff' }} />
                      : dc.icon
                    }
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <Text style={{
                      fontSize: 12, fontWeight: 600, display: 'block',
                      color: active ? '#fff' : '#1a3353',
                      lineHeight: 1.3,
                    }}>
                      {dc.label}
                    </Text>
                    {d.revision_id && (
                      <Tag color={active ? 'default' : 'purple'}
                        style={{
                          fontSize: 9, borderRadius: 3, marginTop: 3, lineHeight: '16px',
                          ...(active ? { background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none' } : {}),
                        }}>
                        Revised
                      </Tag>
                    )}
                    <Text style={{
                      fontSize: 10,
                      color: active ? 'rgba(255,255,255,0.7)' : '#8dabc4',
                      display: 'block', marginTop: 2,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {d.filename}
                    </Text>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom actions in sidebar */}
          <div style={{
            padding: '10px 12px', borderTop: '1px solid #e8ecf1',
            background: '#fff',
          }}>
            <Button
              block size="small" icon={<DownloadOutlined />}
              onClick={() => handleDownload(previewDocs[activeDocIndex])}
              style={{ borderRadius: 6, marginBottom: 6, fontSize: 12 }}
            >
              Download
            </Button>
            <Button
              block size="small" type="link" icon={<EyeOutlined />}
              onClick={() => window.open(previewDocs[activeDocIndex]?.url, '_blank')}
              style={{ fontSize: 12 }}
            >
              Open in New Tab
            </Button>
          </div>
        </div>

        {/* ── RIGHT: Document Viewer (full height vertical) ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', minWidth: 0 }}>

          {/* Top toolbar */}
          <div style={{
            padding: '10px 16px', borderBottom: '1px solid #e8ecf1',
            background: '#fff', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Prev / Next */}
              <Button
                size="small" icon={<LeftOutlined />}
                disabled={activeDocIndex === 0}
                onClick={() => setActiveDocIndex(activeDocIndex - 1)}
                style={{ borderRadius: 6 }}
              />
              <Text style={{ fontSize: 12, color: '#5a6d80' }}>
                {activeDocIndex + 1} / {previewDocs.length}
              </Text>
              <Button
                size="small" icon={<RightOutlined />}
                disabled={activeDocIndex === previewDocs.length - 1}
                onClick={() => setActiveDocIndex(activeDocIndex + 1)}
                style={{ borderRadius: 6 }}
              />
              <Divider type="vertical" style={{ height: 20 }} />
              <Text style={{ fontSize: 13, fontWeight: 600, color: '#1a3353' }}>
                {(docTypeConfig[doc?.doc_type] || docTypeConfig.attachment).label}
              </Text>
            </div>
            <Space size={6}>
              <Tooltip title="Open in New Tab">
                <Button size="small" icon={<EyeOutlined />}
                  onClick={() => window.open(doc.url, '_blank')}
                  style={{ borderRadius: 6 }} />
              </Tooltip>
              <Tooltip title="Download">
                <Button size="small" icon={<DownloadOutlined />}
                  onClick={() => handleDownload(doc)}
                  style={{ borderRadius: 6 }} />
              </Tooltip>
            </Space>
          </div>

          {/* Document content area — fills remaining height */}
          <div style={{ flex: 1, minHeight: 0, background: '#525659', position: 'relative' }}>
            {isImage ? (
              <div style={{
                height: '100%', display: 'flex', justifyContent: 'center',
                alignItems: 'center', overflow: 'auto', padding: 16,
              }}>
                <img
                  src={doc.url}
                  alt={doc.filename}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4 }}
                />
              </div>
            ) : isPdf ? (
              <iframe
                src={`${doc.url}#toolbar=1&navpanes=0&scrollbar=1&view=FitH`}
                title={doc.filename}
                style={{
                  width: '100%', height: '100%', border: 'none',
                  display: 'block',
                }}
              />
            ) : (
              <div style={{
                height: '100%', display: 'flex', flexDirection: 'column',
                justifyContent: 'center', alignItems: 'center',
                background: '#f5f5f5', padding: 40,
              }}>
                <FileTextOutlined style={{ fontSize: 56, color: '#8c8c8c', marginBottom: 16 }} />
                <Text style={{ fontSize: 15, marginBottom: 16, color: '#1a3353' }}>
                  Preview not available for this file type
                </Text>
                <Space>
                  <Button type="primary" icon={<EyeOutlined />}
                    onClick={() => window.open(doc.url, '_blank')}
                    style={{ borderRadius: 8 }}>Open in New Tab</Button>
                  <Button icon={<DownloadOutlined />}
                    onClick={() => handleDownload(doc)}
                    style={{ borderRadius: 8 }}>Download</Button>
                </Space>
              </div>
            )}
          </div>

          {/* Bottom filename bar */}
          <div style={{
            padding: '8px 16px', borderTop: '1px solid #3a3d40',
            background: '#3a3d40', flexShrink: 0,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11 }}>
              {doc.filename}
            </Text>
            <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>
              {doc.file_type?.toUpperCase()}
            </Text>
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════
  //  MAIN RENDER
  // ═══════════════════════════════════════════

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  if (!data) return <Empty description="Submission not found" />;

  const sCfg = statusConfig[data.status] || { color: 'default', label: data.status };
  const catCfg = categoryConfigMap[data.category] || { color: '#8c8c8c', label: data.category || 'Material' };

  const isOnHold = data.status === 'on_hold';
  const canRevise = data.status === 'rejected' && !isAccounts;
  const canEditLpo = data.status !== 'approved' && data.status !== 'deleted';
  // Accounts can act on pending, under review AND held submissions
  const canReview = isAccounts && ['pending_review', 'under_review', 'on_hold'].includes(data.status);
  const revisions = data.revisions || [];
  const revisionCount = data.revision_count || 0;

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>

      {/* ── CSS for edit button hover ── */}
      <style>{`
        .edit-field-btn { opacity: 0 !important; transition: opacity 0.15s; }
        .ant-descriptions-item-content:hover .edit-field-btn,
        .lpo-row:hover .edit-field-btn { opacity: 1 !important; }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack} style={{ borderRadius: 8 }}>Back</Button>
          <Title level={4} style={{ margin: 0, color: '#1a3353' }}>{data.submission_ref}</Title>
          <Tag color={catCfg.color} style={{ borderRadius: 4, fontSize: 12 }}>{catCfg.label}</Tag>
          <Tag icon={sCfg.icon} color={sCfg.color} style={{ borderRadius: 4, fontSize: 13, padding: '2px 12px' }}>{sCfg.label}</Tag>
          {revisionCount > 0 && (
            <Tag icon={<RedoOutlined />} color="purple" style={{ borderRadius: 4, fontSize: 12 }}>
              Rev #{revisionCount}
            </Tag>
          )}
        </Space>
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => handlePreviewAll(0)} style={{ borderRadius: 8 }}>Preview Documents</Button>
        </Space>
      </div>

      {/* Deleted Banner */}
      {data.status === 'deleted' && (
        <Alert type="warning" showIcon icon={<DeleteOutlined />} message={<Text strong>This submission has been deleted</Text>}
          description="Documents have been removed from storage." style={{ marginBottom: 20, borderRadius: 10 }} />
      )}

      {/* ═══ ON HOLD BANNER ═══ */}
      {isOnHold && (
        <Alert
          type="warning" showIcon icon={<PauseCircleOutlined />}
          message={<Text strong>This invoice is on hold with Accounts</Text>}
          description={
            <div>
              <Paragraph style={{ margin: '4px 0 0', fontSize: 13 }}>
                {data.hold_remarks || data.accounts_remarks}
              </Paragraph>
              <Text style={{ fontSize: 11, color: '#8dabc4' }}>
                Held by {data.held_by || 'accounts'} on {formatDateTime(data.held_at)}
                {!isAccounts && ' — nothing to re-upload or revise from your side until the hold is released.'}
              </Text>
            </div>
          }
          style={{ marginBottom: 20, borderRadius: 10, borderColor: HOLD_LINE }}
        />
      )}

      {/* Accounts Remarks Banner — suppressed while on hold (the hold banner already carries the text) */}
      {data.accounts_remarks && !isOnHold && data.status !== 'deleted' && (
        <Alert
          type={data.status === 'approved' ? 'success' : data.status === 'rejected' ? 'error' : 'info'}
          showIcon icon={<AuditOutlined />}
          message={<Text strong>Accounts {data.status === 'approved' ? 'Approved' : data.status === 'rejected' ? 'Rejected' : 'Remarks'}</Text>}
          description={<div><Paragraph style={{ margin: '4px 0 0', fontSize: 13 }}>{data.accounts_remarks}</Paragraph><Text style={{ fontSize: 11, color: '#8dabc4' }}>By {data.reviewed_by} on {formatDateTime(data.reviewed_at)}</Text></div>}
          style={{ marginBottom: 20, borderRadius: 10 }} />
      )}

      {/* ═══ REVISION UPLOAD SECTION ═══ */}
      {canRevise && (
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <RedoOutlined style={{ color: '#722ed1', fontSize: 18 }} />
              <span style={{ fontWeight: 700, color: '#1a3353', fontSize: 15 }}>Submit Revised Invoice</span>
              <Tag color="error" style={{ borderRadius: 4, fontSize: 11, marginLeft: 4 }}>Action Required</Tag>
            </div>
          }
          style={{ borderRadius: 12, border: '2px solid #d3adf7', marginBottom: 24, background: '#faf8ff' }}
        >
          {revisionSubmitting ? (
            <div style={{ textAlign: 'center', padding: '30px 20px' }}>
              <LoadingOutlined style={{ fontSize: 36, color: '#722ed1' }} spin />
              <Title level={5} style={{ color: '#1a3353', margin: '16px 0 8px' }}>Processing Revised Documents...</Title>
              <Text style={{ color: '#5a6d80', fontSize: 13 }}>Extracting data from your revised documents</Text>
              <div style={{ maxWidth: 400, margin: '20px auto 0' }}>
                <Steps current={revisionStep} size="small" items={[
                  { title: 'Uploading' }, { title: 'Extracting Data' }, { title: 'Saving' }, { title: 'Complete' },
                ]} />
              </div>
            </div>
          ) : (
            <div>
              <Alert type="info" showIcon message="This submission was rejected. Upload corrected documents to re-submit for review."
                description={data.accounts_remarks ? `Rejection reason: "${data.accounts_remarks}"` : undefined}
                style={{ marginBottom: 16, borderRadius: 8 }} />

              {/* Revised Invoice (required for all) */}
              <Text strong style={{ fontSize: 13, color: '#1a3353', display: 'block', marginBottom: 8 }}>Revised Invoice <Tag color="error" style={{ borderRadius: 4, fontSize: 10 }}>Required</Tag></Text>
              {revisionFile ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 10, marginBottom: 16 }}>
                  <div style={{ flex: 1 }}>
                    <Text strong style={{ fontSize: 13, display: 'block' }}>{revisionFile.name}</Text>
                    <Text style={{ fontSize: 11, color: '#8dabc4' }}>{(revisionFile.size / 1024).toFixed(1)} KB</Text>
                  </div>
                  <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
                  <Button type="text" danger icon={<DeleteOutlined />} size="small" onClick={() => setRevisionFile(null)} />
                </div>
              ) : (
                <Dragger showUploadList={false}
                  beforeUpload={(file) => { if (file.size > 50 * 1024 * 1024) { message.error('File too large.'); return Upload.LIST_IGNORE; } setRevisionFile(file); return false; }}
                  accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif"
                  style={{ borderRadius: 10, borderColor: '#722ed1', borderWidth: 2, marginBottom: 16 }}>
                  <p style={{ marginBottom: 8 }}><InboxOutlined style={{ fontSize: 36, color: '#722ed1' }} /></p>
                  <p style={{ fontSize: 14, color: '#1a3353', fontWeight: 600, marginBottom: 4 }}>Drop Revised Invoice here</p>
                  <p style={{ color: '#8dabc4', fontSize: 12 }}>PDF, JPG, PNG (max 50 MB)</p>
                </Dragger>
              )}

              {/* Revised Delivery Note (material only, optional) */}
              {data.category === 'material' && (
                <>
                  <Text strong style={{ fontSize: 13, color: '#1a3353', display: 'block', marginBottom: 8 }}>Revised Delivery Note <Tag style={{ borderRadius: 4, fontSize: 10, color: '#8dabc4', background: '#f5f5f5', border: '1px solid #e8ecf1' }}>Optional</Tag></Text>
                  {revisionDnFile ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 10, marginBottom: 16 }}>
                      <div style={{ flex: 1 }}>
                        <Text strong style={{ fontSize: 13, display: 'block' }}>{revisionDnFile.name}</Text>
                        <Text style={{ fontSize: 11, color: '#8dabc4' }}>{(revisionDnFile.size / 1024).toFixed(1)} KB</Text>
                      </div>
                      <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
                      <Button type="text" danger icon={<DeleteOutlined />} size="small" onClick={() => setRevisionDnFile(null)} />
                    </div>
                  ) : (
                    <Dragger showUploadList={false}
                      beforeUpload={(file) => { if (file.size > 50 * 1024 * 1024) { message.error('File too large.'); return Upload.LIST_IGNORE; } setRevisionDnFile(file); return false; }}
                      accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif"
                      style={{ borderRadius: 10, borderColor: '#1a6ed8', borderWidth: 2, marginBottom: 16 }}>
                      <p style={{ fontSize: 13, color: '#1a3353', fontWeight: 600, margin: '8px 0 4px' }}>Drop Revised Delivery Note here</p>
                      <p style={{ color: '#8dabc4', fontSize: 11 }}>PDF, JPG, PNG (max 50 MB)</p>
                    </Dragger>
                  )}
                </>
              )}

              {/* Revised Timesheet (transportation only, optional) */}
              {data.category === 'transportation' && (
                <>
                  <Text strong style={{ fontSize: 13, color: '#1a3353', display: 'block', marginBottom: 8 }}>Revised Timesheet <Tag style={{ borderRadius: 4, fontSize: 10, color: '#8dabc4', background: '#f5f5f5', border: '1px solid #e8ecf1' }}>Optional</Tag></Text>
                  {revisionTsFile ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 10, marginBottom: 16 }}>
                      <div style={{ flex: 1 }}>
                        <Text strong style={{ fontSize: 13, display: 'block' }}>{revisionTsFile.name}</Text>
                        <Text style={{ fontSize: 11, color: '#8dabc4' }}>{(revisionTsFile.size / 1024).toFixed(1)} KB</Text>
                      </div>
                      <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
                      <Button type="text" danger icon={<DeleteOutlined />} size="small" onClick={() => setRevisionTsFile(null)} />
                    </div>
                  ) : (
                    <Dragger showUploadList={false}
                      beforeUpload={(file) => { if (file.size > 50 * 1024 * 1024) { message.error('File too large.'); return Upload.LIST_IGNORE; } setRevisionTsFile(file); return false; }}
                      accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif"
                      style={{ borderRadius: 10, borderColor: '#d48806', borderWidth: 2, marginBottom: 16 }}>
                      <p style={{ fontSize: 13, color: '#1a3353', fontWeight: 600, margin: '8px 0 4px' }}>Drop Revised Timesheet here</p>
                      <p style={{ color: '#8dabc4', fontSize: 11 }}>PDF, JPG, PNG (max 50 MB)</p>
                    </Dragger>
                  )}
                </>
              )}

              <Button
                type="primary" size="large"
                disabled={!revisionFile} onClick={handleSubmitRevision}
                style={{
                  borderRadius: 10, height: 46, fontWeight: 600, fontSize: 15, width: '100%',
                  background: revisionFile ? 'linear-gradient(135deg, #722ed1, #9254de)' : '#d9d9d9', border: 'none',
                }}
              >
                Submit Revised Documents for Re-Review
              </Button>
            </div>
          )}
        </Card>
      )}

      <Row gutter={[20, 20]}>
        <Col xs={24} lg={14}>
          {/* Invoice Details — with inline editing */}
          <Card title={<span style={{ fontWeight: 600, color: '#1a3353' }}><BankOutlined style={{ marginRight: 6, color: '#1a6ed8' }} />Invoice Details {revisionCount > 0 && <Tag color="purple" style={{ fontSize: 10 }}>Updated via Rev #{revisionCount}</Tag>}</span>}
            style={{ borderRadius: 12, border: '1px solid #e8ecf1' }} styles={{ body: { padding: 0 } }}>
            <Descriptions bordered size="small" column={1}
              labelStyle={{ background: '#f8fafc', fontWeight: 600, width: 150, color: '#5a6d80', fontSize: 12 }}
              contentStyle={{ fontSize: 13 }}>

              {/* LPO Number — always editable with prominent edit */}
              <Descriptions.Item label="LPO Number">
                {editingLpo ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Input
                      size="small"
                      value={lpoValue}
                      onChange={(e) => setLpoValue(e.target.value)}
                      onPressEnter={saveLpo}
                      style={{ borderRadius: 6, maxWidth: 200 }}
                      autoFocus
                    />
                    <Button
                      type="primary" size="small" icon={<CheckOutlined />}
                      loading={lpoSaving} onClick={saveLpo}
                      style={{ borderRadius: 6, background: '#52c41a', borderColor: '#52c41a' }}
                    >Save</Button>
                    <Button size="small" icon={<CloseOutlined />} onClick={cancelEditLpo} style={{ borderRadius: 6 }}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="lpo-row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Text strong style={{ fontSize: 14 }}>{data.lpo_number}</Text>
                    {canEditLpo && (
                      <Tooltip title="Edit LPO Number">
                        <Button
                          type="text" size="small" icon={<EditOutlined />}
                          onClick={startEditLpo}
                          className="edit-field-btn"
                          style={{ color: '#1a6ed8', padding: '0 6px', fontSize: 13 }}
                        />
                      </Tooltip>
                    )}
                  </div>
                )}
              </Descriptions.Item>

              <Descriptions.Item label="Vendor Name">
                {renderEditableField('vendor_name', data.vendor_name, { isBold: true })}
              </Descriptions.Item>
              <Descriptions.Item label="Invoice Number">
                {renderEditableField('invoice_number', data.invoice_number, { isTag: true, tagColor: 'blue' })}
              </Descriptions.Item>
              <Descriptions.Item label="Invoice Date">
                {renderEditableField('invoice_date', data.invoice_date)}
              </Descriptions.Item>
              <Descriptions.Item label="Description">
                {renderEditableField('description', data.description)}
              </Descriptions.Item>
              <Descriptions.Item label="Project Code">
                {renderEditableField('project_code', data.project_code)}
              </Descriptions.Item>
              <Descriptions.Item label="Vendor TRN">{data.vendor_trn || '-'}</Descriptions.Item>
              <Descriptions.Item label="Vendor Email">{data.vendor_email || '-'}</Descriptions.Item>
              <Descriptions.Item label="Vendor Phone">{data.vendor_phone || '-'}</Descriptions.Item>
            </Descriptions>
          </Card>

          {/* DN — only for material */}
          {data.category === 'material' && (
          <Card title={<span style={{ fontWeight: 600, color: '#1a3353' }}><FileTextOutlined style={{ marginRight: 6, color: '#1a6ed8' }} />Delivery Note</span>}
            style={{ borderRadius: 12, border: '1px solid #e8ecf1', marginTop: 20 }} styles={{ body: { padding: 0 } }}>
            <Descriptions bordered size="small" column={1}
              labelStyle={{ background: '#f8fafc', fontWeight: 600, width: 150, color: '#5a6d80', fontSize: 12 }}
              contentStyle={{ fontSize: 13 }}>
              <Descriptions.Item label="DN Number">
                {renderEditableField('dn_number', data.dn_number)}
              </Descriptions.Item>
              <Descriptions.Item label="DN Date">
                {renderEditableField('dn_date', data.dn_date)}
              </Descriptions.Item>
              <Descriptions.Item label="Received By">
                {renderEditableField('dn_received_by', data.dn_received_by)}
              </Descriptions.Item>
              <Descriptions.Item label="DN Project Code">
                {renderEditableField('dn_project_code', data.dn_project_code)}
              </Descriptions.Item>
            </Descriptions>
          </Card>
          )}

          {/* Revision History */}
          {revisions.length > 0 && (
            <Card
              title={<span style={{ fontWeight: 600, color: '#1a3353' }}><HistoryOutlined style={{ marginRight: 6, color: '#722ed1' }} />Revision History ({revisions.length})</span>}
              style={{ borderRadius: 12, border: '1px solid #d3adf7', marginTop: 20, background: '#fdfaff' }}
            >
              {revisions.map((rev) => (
                <div key={rev.id} style={{ padding: '14px 16px', border: '1px solid #e8ecf1', borderRadius: 10, marginBottom: 12, background: '#fff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <Space>
                      <Tag color="purple" style={{ borderRadius: 4, fontWeight: 700, fontSize: 13 }}>Revision #{rev.revision_number}</Tag>
                      <Tag color={rev.status === 'approved' ? 'success' : rev.status === 'rejected' ? 'error' : 'gold'} style={{ borderRadius: 4, fontSize: 11 }}>
                        {(rev.status || 'pending_review').replace('_', ' ').toUpperCase()}
                      </Tag>
                    </Space>
                    <Text style={{ fontSize: 11, color: '#8dabc4' }}>{formatDateTime(rev.submitted_at)}</Text>
                  </div>
                  <Row gutter={12}>
                    <Col span={8}><Text style={{ fontSize: 11, color: '#8dabc4', display: 'block' }}>Invoice No.</Text><Text style={{ fontSize: 12 }}>{rev.invoice_number || '-'}</Text></Col>
                    <Col span={8}><Text style={{ fontSize: 11, color: '#8dabc4', display: 'block' }}>Date</Text><Text style={{ fontSize: 12 }}>{rev.invoice_date || '-'}</Text></Col>
                    <Col span={8}><Text style={{ fontSize: 11, color: '#8dabc4', display: 'block' }}>Total</Text><Text strong style={{ fontSize: 12, color: '#1a6ed8' }}>{formatAmount(rev.total_amount)}</Text></Col>
                  </Row>
                  {rev.accounts_remarks && (
                    <div style={{ marginTop: 8, padding: '8px 12px', background: '#fff2f0', borderRadius: 6, border: '1px solid #ffa39e', fontSize: 12 }}>
                      <Text style={{ fontSize: 11, color: '#cf1322' }}>{rev.accounts_remarks}</Text>
                    </div>
                  )}
                  {rev.documents && rev.documents.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      {rev.documents.map(d => (
                        <Tag key={d.id} icon={<FilePdfOutlined />} color="purple" style={{ borderRadius: 4, fontSize: 11, marginBottom: 4 }}>
                          {d.renamed_filename || d.original_filename}
                        </Tag>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </Card>
          )}
        </Col>

        <Col xs={24} lg={10}>
          {/* Amounts */}
          <Card title={<span style={{ fontWeight: 600, color: '#1a3353' }}>Amounts</span>} style={{ borderRadius: 12, border: '1px solid #e8ecf1' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text style={{ color: '#5a6d80' }}>Subtotal (Excl. VAT)</Text><Text>{formatAmount(data.subtotal_amount)}</Text></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text style={{ color: '#5a6d80' }}>VAT ({data.vat_percentage || 5}%)</Text><Text>{formatAmount(data.vat_amount)}</Text></div>
              <Divider style={{ margin: '4px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text strong style={{ fontSize: 15, color: '#1a3353' }}>Total Amount</Text><Text strong style={{ fontSize: 18, color: '#1a6ed8' }}>{formatAmount(data.total_amount)}</Text></div>
            </div>
          </Card>

          {/* Documents */}
          <Card
            title={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 600, color: '#1a3353' }}><PaperClipOutlined style={{ marginRight: 6, color: '#1a6ed8' }} />Documents ({(data.documents || []).length})</span>
              {(data.documents || []).length > 0 && <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handlePreviewAll(0)}>Preview All</Button>}
            </div>}
            style={{ borderRadius: 12, border: '1px solid #e8ecf1', marginTop: 20 }} styles={{ body: { padding: '4px 0' } }}
          >
            {(data.documents || []).map((doc, idx) => {
              const isRevised = !!doc.revision_id;
              const dtLabel = isRevised ? 'revised_invoice' : doc.doc_type;
              const dtCfg = docTypeConfig[dtLabel] || docTypeConfig.attachment;
              return (
                <div key={doc.id} onClick={() => handlePreviewAll(idx)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid #f5f5f5', cursor: 'pointer', transition: 'background 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: dtCfg.color, border: `1px solid ${dtCfg.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, color: '#5a6d80' }}>
                    {dtCfg.label.substring(0, 3).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: 12, color: '#1a3353', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {doc.renamed_filename || doc.original_filename}
                    </Text>
                    <Space size={4}>
                      <Tag style={{ fontSize: 10, borderRadius: 3, margin: 0 }}>{dtCfg.label}</Tag>
                      {isRevised && <Tag color="purple" style={{ fontSize: 9, borderRadius: 3, margin: 0 }}>Revised</Tag>}
                    </Space>
                  </div>
                  <EyeOutlined style={{ color: '#1a6ed8', fontSize: 14 }} />
                </div>
              );
            })}
            {(!data.documents || !data.documents.length) && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No documents" style={{ padding: 20 }} />}
          </Card>

          {/* Meta */}
          <Card style={{ borderRadius: 12, border: '1px solid #e8ecf1', marginTop: 20 }} styles={{ body: { padding: '14px 18px' } }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text style={{ color: '#8dabc4', fontSize: 12 }}>Submitted By</Text><Text style={{ fontSize: 12 }}>{data.submitted_by}</Text></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text style={{ color: '#8dabc4', fontSize: 12 }}>Submitted At</Text><Text style={{ fontSize: 12 }}>{formatDateTime(data.submitted_at)}</Text></div>
              {isOnHold && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text style={{ color: '#8dabc4', fontSize: 12 }}>Held By</Text><Text style={{ fontSize: 12, color: HOLD, fontWeight: 600 }}>{data.held_by || '-'}</Text></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text style={{ color: '#8dabc4', fontSize: 12 }}>Held Since</Text><Text style={{ fontSize: 12, color: HOLD, fontWeight: 600 }}>{formatDateTime(data.held_at)}</Text></div>
                </>
              )}
              {revisionCount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text style={{ color: '#8dabc4', fontSize: 12 }}>Revisions</Text><Tag color="purple" style={{ fontSize: 11, borderRadius: 4 }}>{revisionCount} revision(s)</Tag></div>}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text style={{ color: '#8dabc4', fontSize: 12 }}>Extraction Confidence</Text><Text style={{ fontSize: 12 }}>{data.extraction_confidence ? `${(data.extraction_confidence * 100).toFixed(0)}%` : '-'}</Text></div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Review Panel (Accounts) — also handles held submissions */}
      {canReview && (
        <Card
          title={
            <span style={{ fontWeight: 600, color: '#1a3353' }}>
              {isOnHold
                ? <><PauseCircleOutlined style={{ marginRight: 6, color: HOLD }} />On Hold — Decide Or Release</>
                : <><AuditOutlined style={{ marginRight: 6, color: '#722ed1' }} />Review This Submission</>}
            </span>
          }
          style={{
            borderRadius: 12,
            border: `2px solid ${isOnHold ? HOLD_LINE : '#d3adf7'}`,
            marginTop: 20,
            background: isOnHold ? '#fffdf7' : '#faf8ff',
          }}>
          <TextArea rows={3} placeholder="Review remarks (required for rejection)..." value={reviewRemarks} onChange={e => setReviewRemarks(e.target.value)} style={{ borderRadius: 8, marginBottom: 10 }} />
          <TextArea rows={2}
            placeholder={isOnHold ? 'Optional note when releasing the hold...' : 'Optional hold note — appended to the auto-generated message'}
            value={holdRemarks} onChange={e => setHoldRemarks(e.target.value)}
            style={{ borderRadius: 8, marginBottom: 8 }} />
          {!isOnHold && (
            <div style={{ marginBottom: 16, padding: '8px 12px', background: HOLD_SOFT, border: `1px solid ${HOLD_LINE}`, borderRadius: 8 }}>
              <Text style={{ fontSize: 11, color: '#8dabc4', display: 'block', marginBottom: 2 }}>Holding will show the uploader:</Text>
              <Text style={{ fontSize: 12, color: HOLD }}>{buildHoldMessage(holdRemarks)}</Text>
            </div>
          )}
          <Space wrap>
            <Button type="primary" icon={<CheckCircleOutlined />} size="large" loading={reviewLoading} onClick={() => handleReview('approved')}
              style={{ borderRadius: 8, height: 44, fontWeight: 600, background: '#52c41a', borderColor: '#52c41a' }}>Approve</Button>
            <Button danger icon={<CloseCircleOutlined />} size="large" loading={reviewLoading} onClick={() => handleReview('rejected')}
              style={{ borderRadius: 8, height: 44, fontWeight: 600 }}>Reject</Button>
            {!isOnHold ? (
              <Button icon={<PauseCircleOutlined />} size="large" loading={holdLoading} onClick={handleHold}
                style={{ borderRadius: 8, height: 44, fontWeight: 600, color: HOLD, borderColor: HOLD_LINE }}>Put On Hold</Button>
            ) : (
              <Button icon={<PlayCircleOutlined />} size="large" loading={holdLoading} onClick={handleReleaseHold}
                style={{ borderRadius: 8, height: 44, fontWeight: 600 }}>Release Hold</Button>
            )}
          </Space>
        </Card>
      )}

      {/* Hold override for already-decided submissions (Accounts) */}
      {isAccounts && (data.status === 'approved' || data.status === 'rejected') && (
        <Card
          title={<span style={{ fontWeight: 600, color: '#1a3353' }}><PauseCircleOutlined style={{ marginRight: 6, color: HOLD }} />Put This Invoice Back On Hold</span>}
          style={{ borderRadius: 12, border: `1px solid ${HOLD_LINE}`, marginTop: 20, background: '#fffdf7' }}>
          <Text style={{ fontSize: 12, color: '#5a6d80', display: 'block', marginBottom: 10 }}>
            This overrides the existing {statusConfig[data.status]?.label} decision and tells the uploader the invoice is back with Accounts.
          </Text>
          <TextArea rows={2} placeholder="Optional hold note — appended to the auto-generated message"
            value={holdRemarks} onChange={e => setHoldRemarks(e.target.value)}
            style={{ borderRadius: 8, marginBottom: 10 }} />
          <div style={{ marginBottom: 14, padding: '8px 12px', background: HOLD_SOFT, border: `1px solid ${HOLD_LINE}`, borderRadius: 8 }}>
            <Text style={{ fontSize: 12, color: HOLD }}>{buildHoldMessage(holdRemarks)}</Text>
          </div>
          <Button icon={<PauseCircleOutlined />} size="large" loading={holdLoading} onClick={handleHold}
            style={{ borderRadius: 8, height: 44, fontWeight: 600, background: HOLD, borderColor: HOLD, color: '#fff' }}>
            Put On Hold
          </Button>
        </Card>
      )}

      {/* Audit Trail */}
      {data.audit_logs?.length > 0 && (
        <Card title={<span style={{ fontWeight: 600, color: '#1a3353' }}><AuditOutlined style={{ marginRight: 6, color: '#1a6ed8' }} />Audit Trail</span>}
          style={{ borderRadius: 12, border: '1px solid #e8ecf1', marginTop: 20 }}>
          <Timeline
            items={data.audit_logs.map(log => {
              const ac = auditActionLabels[log.action] || { color: '#8c8c8c', label: log.action };
              return {
                color: ac.color,
                children: (
                  <div>
                    <Text strong style={{ color: ac.color, fontSize: 13 }}>{ac.label}</Text>
                    <Text style={{ fontSize: 12, color: '#5a6d80', marginLeft: 8 }}>by {log.performed_by}</Text>
                    <Text style={{ fontSize: 11, color: '#8dabc4', display: 'block' }}>{formatDateTime(log.performed_at)}</Text>
                    {log.details?.from && log.details?.to && (
                      <div style={{ marginTop: 4, fontSize: 12, color: '#5a6d80' }}>
                        {statusConfig[log.details.from]?.label || log.details.from} → {statusConfig[log.details.to]?.label || log.details.to}
                      </div>
                    )}
                    {log.details?.auto_message && (
                      <div style={{ marginTop: 4, padding: '6px 10px', background: HOLD_SOFT, borderRadius: 6, border: `1px solid ${HOLD_LINE}`, fontSize: 12 }}>
                        {log.details.auto_message}
                      </div>
                    )}
                    {log.details?.remarks && <div style={{ marginTop: 4, padding: '6px 10px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e8ecf1', fontSize: 12 }}>{log.details.remarks}</div>}
                    {log.details?.revision_number && <Tag color="purple" style={{ marginTop: 4, fontSize: 10 }}>Revision #{log.details.revision_number}</Tag>}
                  </div>
                ),
              };
            })}
          />
        </Card>
      )}

      {/* ═══ DOCUMENT PREVIEW DRAWER (Vertical, from right) ═══ */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <EyeOutlined style={{ color: '#1a6ed8' }} />
            <span style={{ fontWeight: 600, fontSize: 15, color: '#1a3353' }}>
              Document Preview
            </span>
            <Tag color="blue" style={{ borderRadius: 4, fontSize: 11 }}>
              {data?.submission_ref}
            </Tag>
          </div>
        }
        placement="right"
        open={previewVisible}
        onClose={() => setPreviewVisible(false)}
        width="85vw"
        styles={{
          body: { padding: 0, overflow: 'hidden' },
          header: { borderBottom: '1px solid #e8ecf1', padding: '12px 20px' },
        }}
        destroyOnClose
      >
        <div style={{ height: 'calc(100vh - 56px)' }}>
          {renderDrawerPreview()}
        </div>
      </Drawer>
    </div>
  );
};

export default SubmissionDetail;